import { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';

import useAuth from '../../../auth/useAuth';
import AuthService from '../../../auth/AuthService';
import { useTour } from '../../../context/TourContext';
import { updateUserTournamentOnboarding } from '../../Login/api';

import {
  tournamentsRoundsQuery,
  tournamentsMatchupsQuery,
  tournamentsTeamsQuery,
  updateTournamentRound,
  summarizeTournamentsScores,
  tournamentsTeamScoresQuery,
  getTournamentsUserScores,
  getTournamentsTopPerformer,
  configureNextRound,
  moveStudentsBulk,
  gameDecisionsQuery,
  gamesTransactionQuery,
  itemsQuery,
  principlesDecisionsQuery
} from './api';
import { generalInfoQuery } from '../settings/assignments/api';
import { consolidatedInsightsQuery } from '../insights/api';

import Layout from '../../UI/introGameLayout/layout';

import ReviewSessionHeader from '../../UI/reviewSession/header';
import ReviewSessionTournamentBracket from '../../UI/reviewSession/tournamentBracket';
import ReviewSessionGamesPreviewModal from '../../UI/reviewSession/gamesPreviewModal';
import {
  CelebrationModal,
  LeaderCelebration,
  WinnersLosers,
  Champions,
  SecondPlace,
  TopPerformer
} from '../../UI/reviewSession/celebrations';
import { roundNames } from '../../UI/reviewSession/data';
import InstructorTransactionsModal from '../../UI/instructor/decisionInsights/transactionsModal';
import InstructorClassifyingNormalModal from '../../UI/instructor/decisionInsights/classifyingNormalModal';
import Alert from '../../UI/alert';

import { 
  gameTypes,
  summaryDecisionBreakdownElementsToDiplay,
  classificationElementsHeaders,
  classificationAllOtherAccountsHeaders,
  normalAllOtherAccountsHeaders,
  doubleEntryElementsHeaders,
  principlesElementsHeaders
} from './data';

import './styles.css';

export default function InstructorReviewSession() {
  const params = useParams();
  const tournamentId = Number(params.tournamentId);

  const { saveCurrentGameId, getUser } = useAuth();
  const { startTour, isActive: isTourActive } = useTour();
  const tourStartedRef = useRef(false);

  const [tournament, setTournament] = useState(null);
  const [currentRound, setCurrentRound] = useState(1);
  const [isGamesPreviewModalVisible, setIsGamesPreviewModalVisible] = useState(false);
  const [isUpdateScoresButtonLoading, setIsUpdateScoresButtonLoading] = useState(false);
  const [isScoresUpdated, setIsScoresUpdated] = useState(false);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [alertTitle, setAlertTitle] = useState('Error');
  const [alertText, setAlertText] = useState('');
  const [isReviewButtonLoading, setIsReviewButtonLoading] = useState(false);
  const [isReviewButtonDisabled, setIsReviewButtonDisabled] = useState(true);
  const [isClassyfyingNormalModalShown, setIsClassyfyingNormalModalShown] = useState(false);
  const [classificationElementsData, setClassificationElementsData] = useState([]);
  const [classificationAllOtherAccountsData, setClassificationAllOtherAccountsData] = useState([]);
  const [normalAllOtherAccountsData, setNormalAllOtherAccountsData] = useState([]);
  const [isTransactionsModalShown, setIsTransactionsModalShown] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const [transactionsTableHeaders, setTransactionsTableHeaders] = useState(doubleEntryElementsHeaders);
  const [celebration, setCelebration] = useState(null);

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const { data: tournamentsRounds } = useQuery(tournamentsRoundsQuery({tournamentId: tournament?.id}));
  const { data: tournamentsMatchups, refetch: refetchTournamentsMatchups } = useQuery(tournamentsMatchupsQuery({tournamentId: tournament?.id}));
  const { data: tournamentsTeams } = useQuery(tournamentsTeamsQuery({tournamentId: tournament?.id}));
  const { data: tournamentsTeamScores, refetch: refetchTournamentsTeamsScores } = useQuery(tournamentsTeamScoresQuery({tournamentId: tournament?.id}));

  const { refetch: refetchItems } = useQuery({
    ...itemsQuery(),
    enabled: false,
  });
  const { refetch: refetchPrincipleDecisions } = useQuery({
    ...principlesDecisionsQuery(),
    enabled: false,
  });

  const tournamentRoundGames = useMemo(() => {
    if (!tournamentsRounds?.length || !generalInfo?.games) return [];
    return tournamentsRounds.map(round => {
      const game = generalInfo.games.find(g => g.id === round.game_id) || null;
      return {
        ...round,
        gameType: game?.game_type || null,
        game,
      };
    });
  }, [tournamentsRounds, generalInfo?.games]);

  const queryClient = useQueryClient();

  const updateTournamentRoundMutation = useMutation({
    mutationFn: (data) => updateTournamentRound(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['tournamentsRounds', tournament?.id],
        exact: true
      });
    }
  });

  const summarizeTournamentsScoresMutation = useMutation({
    mutationFn: (data) => summarizeTournamentsScores(data)
  });
  
  const moveStudentsBulkMutation = useMutation({
    mutationFn: (data) => moveStudentsBulk(data)
  });

  const configureNextRoundMutation = useMutation({
    mutationFn: (data) => configureNextRound(data)
  });

  useEffect(() => {
    if(generalInfo?.tournaments && tournamentId) {
      const currentTournament = generalInfo.tournaments.find(tournament => tournament.id === tournamentId);

      setTournament(currentTournament);
    }
  }, [generalInfo?.tournaments, tournamentId]);

  // Trigger the tournament-overview tour once for users who haven't
  // seen it yet. The field `onboarded_tournament` on the user record
  // is our source of truth — missing or false means "not yet onboarded".
  useEffect(() => {
    if (tourStartedRef.current) return;
    if (isTourActive) return;
    if (!generalInfo || !tournament || !tournamentsRounds?.length) return;

    const cachedUser = getUser();
    if (!cachedUser?.id) return;

    // Prefer the freshly-fetched user from generalInfo.users so a backend
    // flip of onboarded_tournament is respected even if localStorage is stale.
    const usersArr = Array.isArray(generalInfo.users)
      ? generalInfo.users
      : generalInfo.users ? [generalInfo.users] : [];
    const freshUser = usersArr.find(u => u.id === cachedUser.id);

    const isOnboarded = freshUser
      ? freshUser.onboarded_tournament === true
      : cachedUser.onboarded_tournament === true;

    if (isOnboarded) return;

    // Sync localStorage with the freshly-fetched value so subsequent checks
    // don't rely on a stale cached user.
    if (freshUser && cachedUser.onboarded_tournament !== freshUser.onboarded_tournament) {
      AuthService.setUser({ ...cachedUser, onboarded_tournament: freshUser.onboarded_tournament });
    }

    tourStartedRef.current = true;
    startTour('tournament-overview', {
      data: {
        tournament,
      },
      onComplete: async () => {
        const current = AuthService.getUser();
        if (current?.onboarded_tournament === true) return;
        try {
          await updateUserTournamentOnboarding({ id: cachedUser.id });
          AuthService.setUser({ ...current, onboarded_tournament: true });
        } catch (err) {
          console.error('Failed to mark tournament onboarding complete:', err);
        }
      },
    });
  }, [generalInfo, tournament, tournamentsRounds, isTourActive, startTour, getUser]);
  
  useEffect(() => {
    if (!tournamentsRounds?.length) return;

    const activeRound = tournamentsRounds.find(round => round.is_active);
    const unfinishedRound = tournamentsRounds.find(round => !round.is_finished);
    const lastRound = tournamentsRounds[tournamentsRounds.length - 1];

    const roundToSet = activeRound || unfinishedRound || lastRound;

    setCurrentRound(prev => {
      if (prev !== roundToSet.number) {
        setIsScoresUpdated(false);
        return roundToSet.number;
      }
      return prev;
    });

    const hasTeamScoresForRound = tournamentsTeamScores?.some(
      score => score.round_number === roundToSet.number
    );

    setIsReviewButtonDisabled(roundToSet.is_active || !hasTeamScoresForRound);
  }, [tournamentsRounds, tournamentsTeamScores]);

  const getRoundRows = (roundCount) => {
    const rounds = ['Sweet 16', 'Elite 8', 'Final 4', 'Championship'];
    const ordinals = ['First', 'Second', 'Third', 'Final'];

    return rounds
      .slice(4 - roundCount)
      .map((name, i, arr) => {
        const order = i === arr.length - 1 ? 'Final' : ordinals[i];
        return {
          name: `${order} Round`,
          stage: `(${name})`,
          gameId: tournamentRoundGames[i]?.game_id || null,
          gameType: tournamentRoundGames[i]?.gameType || null,
          index: i + 1,
          completed: false,
        };
      });
  };

  // Map a tournament game_type to the direct level-1 URL. Preview from
  // a tournament round always targets level 1, so we skip /acc/transition
  // (which would fetch games + sessions + scenarioLevels + run its own
  // routing logic — ~4s of latency for a decision we already know).
  const getPreviewLevelUrl = (gameType) => {
    if (gameType?.includes('Account Classification')) return '/classification/level/1';
    if (gameType?.includes('Normal Balance')) return '/normal/level/1';
    if (gameType?.includes('Balanced Equation')) return '/balanced-equation/level/1';
    if (gameType?.includes('The Principles')) return '/the-principles/level/1';
    return '/acc/transition';
  };

  const handlePreviewButtonClick = () => {
    // Bypass the game-selection modal and open the current round's game directly.
    const currentRoundGame = tournamentRoundGames[currentRound - 1];
    if (!currentRoundGame?.game_id || !currentRoundGame?.gameType) return;

    saveCurrentGameId(currentRoundGame.game_id, currentRoundGame.gameType);
    window.open(getPreviewLevelUrl(currentRoundGame.gameType), '_blank');
  }

  const handleOverlayModalClick = () => {
    setIsGamesPreviewModalVisible(false);
    setIsClassyfyingNormalModalShown(false);
    setIsTransactionsModalShown(false);
  }

  const handleGamesOverlayModalSelectButtonClick = async (row) => {
    saveCurrentGameId(row.gameId, row.gameType);
    window.open(getPreviewLevelUrl(row.gameType), '_blank');
  }

  const handleRoundStatusChange = async () => {
    const currentRoundId = tournamentsRounds[currentRound - 1]?.id;
    const currentRoundStatus = tournamentsRounds[currentRound - 1]?.is_active;

    try {
      await updateTournamentRoundMutation.mutateAsync({
        id: currentRoundId,
        isActive: !currentRoundStatus
      });
    } catch (error) {
      console.error('Failed to update round status:', error);
      const status = error?.response?.status;
      setAlertTitle(status === 401 || status === 403 ? 'Not Allowed' : 'Error');
      setAlertText(
        status === 401 || status === 403
          ? 'This round cannot be reopened. It may already be finalized.'
          : 'Could not update the round status. Please try again.'
      );
      setIsAlertVisible(true);
      return;
    }

    // Scores are summarized only via the explicit "Update Scores" button
    // or when advancing to the next round — not here.
    const hasTeamScoresForRound = tournamentsTeamScores?.some(
      score => score.round_number === currentRound
    );

    setIsReviewButtonDisabled(!currentRoundStatus || !hasTeamScoresForRound);
  }

  const handleUpdateScoresButtonClick = async () => {
    setIsUpdateScoresButtonLoading(true);

    await summarizeTournamentsScoresMutation.mutateAsync({
      roundId: tournamentsRounds[currentRound - 1]?.id
    });

    const { data: updatedTeamScores } = await refetchTournamentsTeamsScores();
    const hasTeamScoresForRound = updatedTeamScores?.some(
      score => score.round_number === currentRound
    );
    const currentRoundStatus = tournamentsRounds[currentRound - 1]?.is_active;

    setIsReviewButtonDisabled(currentRoundStatus || !hasTeamScoresForRound);

    setIsUpdateScoresButtonLoading(false);
    setIsScoresUpdated(true);

    if (hasTeamScoresForRound) {
      const leadingTeamIds = updatedTeamScores
        .filter(s => s.round_number === currentRound && s.team_result === 'WINNER')
        .map(s => s.team_id);
      const leadingTeams = tournamentsTeams.filter(team => leadingTeamIds.includes(team.id));
      if (leadingTeams.length) {
        const roundCount = tournament?.round_count || tournamentsRounds?.length || 0;
        const roundName = roundNames[roundNames.length - roundCount + (currentRound - 1)] || '';
        setCelebration({ type: 'leader', leadingTeams, roundName });
      }
    }
  }

  const handleNextRoundButtonClick = async () => {
    const currentRoundIsActive = tournamentsRounds?.[currentRound - 1]?.is_active;
    if (currentRoundIsActive) {
      setAlertTitle('Round is Active');
      setAlertText('Round is still active. Close round and update scores first.');
      setIsAlertVisible(true);
      return;
    }

    const currentRoundId = tournamentsRounds[currentRound - 1]?.id;
    const nextRoundId = tournamentsRounds[currentRound]?.id;
    const currentRoundMatchups = tournamentsMatchups.filter(matchup => matchup.round_id === currentRoundId);

    const roundCount = tournament?.round_count || tournamentsRounds?.length || 0;
    const isChampionshipRound = currentRound === roundCount;

    // Summarize scores before closing the round. Once the round is
    // marked finished the backend may reject re-summarization, so this
    // must run first. Covers both Next Round and Process Final Results.
    let effectiveTeamScores = tournamentsTeamScores;
    try {
      await summarizeTournamentsScoresMutation.mutateAsync({ roundId: currentRoundId });
      const { data: updatedTeamScores } = await refetchTournamentsTeamsScores();
      effectiveTeamScores = updatedTeamScores;
      setIsScoresUpdated(true);
    } catch (error) {
      console.error('Failed to summarize scores before advancing round:', error);
      setAlertTitle('Error');
      setAlertText('Could not summarize scores. Please try again.');
      setIsAlertVisible(true);
      return;
    }

    const currentRoundTeamScores = effectiveTeamScores.filter(score => score.round_number === currentRound);
    if (!currentRoundTeamScores.length) {
      setAlertTitle('Error');
      setAlertText('No scores available for this round.');
      setIsAlertVisible(true);
      return;
    }

    // Guard against advancing when nobody actually scored — usually means
    // the round wasn't played (or scores haven't been pulled in yet).
    // Runs before the finish mutation so a stalled round can still be
    // reopened and played.
    const anyTeamHasPositiveScore = currentRoundTeamScores.some(
      score => (score.team_total_points || 0) > 0
    );
    if (!anyTeamHasPositiveScore) {
      setAlertTitle('No scores yet');
      setAlertText('No team has scored above 0 in this round. Play the round or update the scores before advancing.');
      setIsAlertVisible(true);
      return;
    }

    await updateTournamentRoundMutation.mutateAsync({
      id: currentRoundId,
      isActive: false,
      isFinished: true
    });

    // Championship: no next round to configure. Show the rotating
    // celebration only if the final matchup has an explicit WINNER and
    // LOSER (tied championships get no popup per celebration rules).
    if (isChampionshipRound) {
      const championshipMatchup = currentRoundMatchups[0];
      if (championshipMatchup) {
        const matchupScores = currentRoundTeamScores.filter(s => s.matchup_id === championshipMatchup.id);
        const winnerScore = matchupScores.find(s => s.team_result === 'WINNER');
        const loserScore  = matchupScores.find(s => s.team_result === 'LOSER');
        const winnerTeam = winnerScore ? tournamentsTeams.find(t => t.id === winnerScore.team_id) : null;
        const loserTeam  = loserScore  ? tournamentsTeams.find(t => t.id === loserScore.team_id)  : null;
        if (winnerTeam && loserTeam) {
          // Pull the tournament's top performer (highest cumulative
          // user_total_points across rounds). Sum the per-round rows
          // for the third celebration frame; pluck the user name off
          // the first row (every row belongs to the same user). Fail
          // soft — if the call errors out we still surface the
          // champion+runner-up frames.
          let topPerformerName = null;
          let topPerformerPoints = null;
          try {
            const topPerformerRows = await getTournamentsTopPerformer({ tournamentId });
            if (Array.isArray(topPerformerRows) && topPerformerRows.length) {
              topPerformerName = topPerformerRows[0]?.user || null;
              topPerformerPoints = topPerformerRows.reduce(
                (sum, row) => sum + (row.user_total_points || 0),
                0
              );
            }
          } catch (err) {
            console.error('Failed to fetch tournament top performer:', err);
          }

          setCelebration({
            type: 'championshipPair',
            winnerTeam,
            loserTeam,
            topPerformerName,
            topPerformerPoints
          });
        }
      }
      return;
    }

    const mathupsNumber = currentRoundMatchups?.length / 2 || 0;

    await configureNextRoundMutation.mutateAsync({
      roundId: nextRoundId,
      mathupsNumber
    });

    // Backend has created next-round matchups with winning teams already
    // assigned. Refetch so the cache sees the new assignments.
    const { data: updatedMatchups = [] } = await refetchTournamentsMatchups();
    const nextRoundMatchups = updatedMatchups.filter(m => m.round_id === nextRoundId);

    // Redistribution uses all advancing teams (including tie-break picks)
    // so every next-round slot absorbs students from eliminated teams.
    const advancingTeamIds = nextRoundMatchups
      .flatMap(m => [m.team1_id, m.team2_id])
      .filter(Boolean);
    const advancingSet = new Set(advancingTeamIds);
    const eliminatedTeamIds = currentRoundMatchups
      .flatMap(m => [m.team1_id, m.team2_id])
      .filter(Boolean)
      .filter(id => !advancingSet.has(id));

    if (!advancingTeamIds.length) return;

    const loserUserIds = tournamentsTeams
      .filter(team => eliminatedTeamIds.includes(team.id))
      .flatMap(team => team.user_ids);

    const shuffledLoserUserIds = [...loserUserIds].sort(() => Math.random() - 0.5);

    const moveOperations = advancingTeamIds.reduce((ops, teamId, index) => {
      const usersForTeam = shuffledLoserUserIds.filter((_, i) => i % advancingTeamIds.length === index);
      if (usersForTeam.length > 0) {
        ops.push({
          destination_team_id: teamId,
          student_ids: usersForTeam
        });
      }
      return ops;
    }, []);

    if (moveOperations.length) {
      try {
        await moveStudentsBulkMutation.mutateAsync(moveOperations);
      } catch (err) {
        console.error('Failed to move losing students to winning teams', err);
      }

      // Refetch teams so the next round-advance reads up-to-date
      // user_ids — without this, absorbed students from prior rounds
      // get stranded on eliminated teams and never get redistributed.
      await queryClient.invalidateQueries({ queryKey: ['tournamentsTeams', tournament?.id] });
    }

    // Celebration rosters use team_result strictly — tied teams (even
    // when they advance via backend tie-break) don't appear in either
    // column of the Winners & Losers popup.
    const winnerTeamIdsForCelebration = currentRoundTeamScores
      .filter(s => s.team_result === 'WINNER')
      .map(s => s.team_id);
    const loserTeamIdsForCelebration = currentRoundTeamScores
      .filter(s => s.team_result === 'LOSER')
      .map(s => s.team_id);
    const winnerTeams = tournamentsTeams.filter(t => winnerTeamIdsForCelebration.includes(t.id));
    const loserTeams  = tournamentsTeams.filter(t => loserTeamIdsForCelebration.includes(t.id));
    const roundName = roundNames[roundNames.length - roundCount + (currentRound - 1)] || '';
    if (winnerTeams.length || loserTeams.length) {
      setCelebration({ type: 'winnersLosers', winnerTeams, loserTeams, roundName });
    }
  }

  const handleConfirmAlertButtonClick = () => {
    setIsAlertVisible(false);
  }

  const mergeDecisions = (allDecisionsData) => {
    return allDecisionsData
      .flat()
      .reduce((acc, item) => {
        const existing = acc.find(el => el.question === item.question && el.level === item.level);

        if (existing) {
          item.options.forEach(opt => {
            const existingOpt = existing.options.find(o => o.option === opt.option);
            if (existingOpt) {
              existingOpt.count_users += opt.count_users;
            } else {
              existing.options.push({ ...opt });
            }
          });
        } else {
          acc.push(JSON.parse(JSON.stringify(item)));
        }

        return acc;
      }, []);
  };

  const mergeDesignDecisions = (decisions) => {
    if (!decisions?.length) return null;

    const base = decisions.reduce((min, cur) => (cur.question_id < min.question_id ? cur : min), decisions[0]);
    const merged = { ...JSON.parse(JSON.stringify(base)), item_options: [] };

    const optionsMap = new Map();

    decisions.forEach(d => {
      (d.item_options || []).forEach(opt => {
        const optionKey = opt.name ?? String(opt.id);
        if (!optionsMap.has(optionKey)) {
          optionsMap.set(optionKey, {
            id: opt.id,
            name: opt.name,
            selected_change_options: [],
            _mergeMap: new Map(),
          });
        }

        const target = optionsMap.get(optionKey);

        (opt.selected_change_options || []).forEach(sco => {
          const mergeKey = sco.selected_option; // merge by selected_option (e.g., "Increase")
          if (target._mergeMap.has(mergeKey)) {
            target._mergeMap.get(mergeKey).count_users += sco.count_users || 0;
          } else {
            target._mergeMap.set(mergeKey, { ...sco });
          }
        });
      });
    });

    merged.item_options = Array.from(optionsMap.values()).map(({ _mergeMap, ...rest }) => ({
      ...rest,
      selected_change_options: Array.from(_mergeMap.values()),
    }));

    return merged;
  };

  const checkIsEquationCorrect = (itemOption, option, debitItem, creditItem, validOptions) => {
    const isDebitRelated = debitItem.parent_objects.some(obj => obj.name === itemOption.name);
    const isCreditRelated = creditItem.parent_objects.some(obj => obj.name === itemOption.name);
    const selectedOption = option.selected_option;

    if (validOptions.includes('Increase')) {
      if (itemOption.name === 'Assets') {
        return (isDebitRelated && selectedOption === 'Increase') || 
              (isCreditRelated && selectedOption === 'Decrease');
      }
      return (isDebitRelated && selectedOption === 'Decrease') || 
            (isCreditRelated && selectedOption === 'Increase');
    }

    return (isDebitRelated && selectedOption === 'Debit') || 
          (isCreditRelated && selectedOption === 'Credit');
  };

  const processClassificationNormalDecisionData = (decisionInsights, targetHeaders) => {
    return decisionInsights
      .map(decisionInsight => {
        const row = {
          question: decisionInsight.question,
        };

        let totalValue = 0;

        // Only process options that match target headers
        const relevantOptions = decisionInsight?.options.filter(option =>
          targetHeaders.includes(option.option)
        );

        // If no relevant options, return null to filter out later
        if (relevantOptions.length === 0) return null;

        relevantOptions.forEach(insightsOption => {
          row[insightsOption.option] = {
            value: insightsOption.count_users,
            isCorrect: insightsOption.is_correct,
          };

          totalValue += insightsOption.count_users;
        });

        relevantOptions.forEach(insightsOption => {
          const itemData = row[insightsOption.option];
          const threshold = Math.min(10, totalValue * 0.2);

          if (itemData.isCorrect) {
            itemData.background = '#D1E9D6';
          } else if (itemData.value && itemData.value >= threshold) {
            itemData.background = '#FD9595';
          } else {
            itemData.background = '#FFFFFF';
          }
        });

        return row;
      })
      .filter(row => row !== null);
  };

  const processEquationDecision = (decision, row, debitItem, creditItem, validOptions) => {
    decision.item_options.forEach(itemOption => {
      const filteredOptions = itemOption.selected_change_options.filter(
        option => option.selected_change === null && validOptions.includes(option.selected_option)
      );

      filteredOptions.forEach(option => {
        const isCorrect = checkIsEquationCorrect(itemOption, option, debitItem, creditItem, validOptions);
        
        let value;
        let backgroundColor = isCorrect ? '#D1E9D6' : '#FAEDED';
        
        if (!isCorrect && option.count_users >= 5) {
          value = `<span class="white">${option.selected_option}<span class="decision-insights-count">${option.count_users}</span></span>`;
          backgroundColor = '#FD9595';
        } else {
          value = option.selected_option;
        }

        const optionItemName = itemOption.name;
        if (!summaryDecisionBreakdownElementsToDiplay.includes(optionItemName)) {
          if (!row[optionItemName]) {
            row[optionItemName] = [];
          }
          row[optionItemName].push({ value, isCorrect, background: backgroundColor });
        }
      });
    });
  };

  const handleReviewButtonClick = async () => {
    const currentRoundData = tournamentsRounds?.[currentRound - 1];
    if(currentRoundData?.is_active) {
      setAlertTitle('Round is Active');
      setAlertText('To review results, close the round and update scores.');
      setIsAlertVisible(true);
      return;
    }

    // Insufficient-data guard based on per-user participation for the
    // round: if fewer than 20% of users scored any points, the review
    // insights would be misleading.
    let userScoresForRound = [];
    try {
      userScoresForRound = await getTournamentsUserScores({
        tournamentId: tournament?.id,
        roundNumber: currentRound
      });
    } catch (error) {
      console.error('Failed to fetch user scores for insufficient-data check:', error);
    }
    const usersWithPoints = userScoresForRound.filter(score => score.user_total_points > 0);
    if (userScoresForRound.length === 0 || (usersWithPoints.length / userScoresForRound.length) < 0.2) {
      setAlertTitle('Insufficient Data');
      setAlertText('Fewer than 20% of students made decisions this round.');
      setIsAlertVisible(true);
      return;
    }

    setIsReviewButtonLoading(true);
    const currentRoundGame = tournamentRoundGames[currentRound - 1];
    const currentGameType = gameTypes[currentRoundGame?.gameType];

    if(currentGameType === 'classification' || currentGameType === 'normal') {
      const consolidatedInsights = await queryClient.fetchQuery(
        consolidatedInsightsQuery()
      ).catch(error => {
        console.error('Error fetching consolidated insights:', error);
        return null;
      });

      if(currentGameType === 'classification') {
        const classificationInsights = consolidatedInsights?.classification || [];
        const elementsData = processClassificationNormalDecisionData(classificationInsights, classificationElementsHeaders);
        const allOtherAccountsData = processClassificationNormalDecisionData(classificationInsights, classificationAllOtherAccountsHeaders);
        setClassificationElementsData(elementsData);
        setClassificationAllOtherAccountsData(allOtherAccountsData);

        setNormalAllOtherAccountsData([]);
      } else {
        const normalInsights = consolidatedInsights?.normal_balance || [];
        const elementsOtherAccountsNormalData = processClassificationNormalDecisionData(normalInsights, normalAllOtherAccountsHeaders);

        setClassificationElementsData([]);
        setClassificationAllOtherAccountsData([]);

        setNormalAllOtherAccountsData(elementsOtherAccountsNormalData);
      }
      setIsClassyfyingNormalModalShown(true);
      setIsReviewButtonLoading(false);
      return;
    }

    const gameDecisions = await queryClient.fetchQuery(
      gameDecisionsQuery({
        gameType: currentGameType,
        gameId: currentRoundGame?.game_id
      })
    ).catch(error => {
      console.error('Error fetching game decisions:', error);
      return null;
    });

    if(currentGameType === 'equation') {
      const { data: items } = await refetchItems();

      const uniqueTransactionIds = [...new Set(
        gameDecisions.map(d => d.transaction_id)
      )];

      const decisionLookup = gameDecisions.reduce((acc, item) => {
        const key = `${item.transaction_id}-${item.level}`;
        acc[key] = acc[key] || [];
        acc[key].push(item);
        return acc;
      }, {});

      const findDecisionInsightByDesign = (transactionId, level) => {
        const key = `${transactionId}-${level}`;
        return decisionLookup[key] || null;
      };

      const transactionsData = [];

      for (const transactionId of uniqueTransactionIds) {
        const transaction = await queryClient.fetchQuery(
          gamesTransactionQuery({ id: transactionId })
        ).catch(error => {
          console.error(`Error fetching transaction ${transactionId}:`, error);
          return null;
        });

        if (!transaction?.data) continue;

        const relatedTransaction = transaction.data;
        const designOneDecisions = findDecisionInsightByDesign(transactionId, "1");
        const designTwoDecisions = findDecisionInsightByDesign(transactionId, "2");

        const designOneDecision = mergeDesignDecisions(designOneDecisions);
        const designTwoDecision = mergeDesignDecisions(designTwoDecisions);

        if (!designOneDecision && !designTwoDecision) continue;

        const row = {
          question: gameDecisions.find(d => d.transaction_id === transactionId).question,
        };

        const debitItem = items.find(item => item.id === relatedTransaction.debit);
        const creditItem = items.find(item => item.id === relatedTransaction.credit);

        if (designOneDecision) {
          processEquationDecision(designOneDecision, row, debitItem, creditItem, ['Increase', 'Decrease']);
        }
        if (designTwoDecision) {
          processEquationDecision(designTwoDecision, row, debitItem, creditItem, ['Debit', 'Credit']);
        }

        transactionsData.push(row);
      }
      setTransactionsData(transactionsData);
      setTransactionsTableHeaders(doubleEntryElementsHeaders);
      setIsTransactionsModalShown(true);
    } else if (currentGameType === 'principles') {
      const { data: principleDecisions } = await refetchPrincipleDecisions();

      const header = 'Impact on Profits';

      const transactionsData = [];

      gameDecisions.forEach(decisionInsight => {
        const row = {
          question: decisionInsight.question,
          [header]: []
        };

        // Process all options, not just the first one
        decisionInsight.options.forEach(option => {
          const relatedDecision = principleDecisions.find(decision => 
            decision.question === decisionInsight.question_id && decision.selected_option === option.option
          );
          const isCorrect = relatedDecision?.is_correct;

          let value;
          let backgroundColor = isCorrect ? '#D1E9D6' : '#FAEDED';
          
          if (!isCorrect && option.count_users >= 5) {
            value = `<span class="white">${option.option}<span class="decision-insights-count">${option.count_users}</span></span>`;
            backgroundColor = '#FD9595';
          } else {
            value = option.option;
          }

          row[header].push({
            value,
            isCorrect,
            background: backgroundColor,
          });
        });
        
        transactionsData.push(row);
      });
      
      setTransactionsData(transactionsData);
      setTransactionsTableHeaders(principlesElementsHeaders);
      setIsTransactionsModalShown(true);
    }
    setIsReviewButtonLoading(false);    
  }

  return (
    <Layout
      isHeaderVisible={true}
      isContentVertical={true}
      backgroundColor="#FFF8EB"
      className="instructor-review-session-layout"
      middleHeaderButtonText="Preview"
      middleHeaderButtonDataTourId="preview-button"
      onMiddleHeaderButtonClick={handlePreviewButtonClick}
    >
      {
        !generalInfo?.tournaments || !tournamentsRounds || !tournamentsMatchups || !tournamentsTeams
        ? <span className="loader"></span>
        : <>
            <ReviewSessionHeader 
              roundCount={tournament?.round_count}
              currentRound={currentRound}
              rounds={tournamentsRounds}
              onRoundStatusChange={handleRoundStatusChange}
              onUpdateButtonClick={handleUpdateScoresButtonClick}
              isUpdateScoresButtonLoading={isUpdateScoresButtonLoading}
              onNextRoundButtonClick={handleNextRoundButtonClick}
            />
            {
              isGamesPreviewModalVisible && 
                <ReviewSessionGamesPreviewModal 
                  onClose={handleOverlayModalClick}
                  roundRows={getRoundRows(tournament?.round_count)}
                  onSelectButtonClick={handleGamesOverlayModalSelectButtonClick}
                  title="Select The Exercise to Preview"
                />
            }
            {isClassyfyingNormalModalShown && (
              <InstructorClassifyingNormalModal
                onCloseButtonClick={handleOverlayModalClick}
                elementsData={classificationElementsData}
                elementsHeaders={classificationElementsHeaders}
                allOtherAccountsData={classificationAllOtherAccountsData}
                allOtherAccountsHeaders={classificationAllOtherAccountsHeaders}
                elementsOtherAccountsNormalData={normalAllOtherAccountsData}
                elementsOtherAccountsNormalHeaders={normalAllOtherAccountsHeaders}
              />
            )}
            {isTransactionsModalShown && (
              <InstructorTransactionsModal
                onCloseButtonClick={handleOverlayModalClick}
                tableHeaders={transactionsTableHeaders}
                transactionsData={transactionsData}
              />
            )}
            {
              isAlertVisible &&
                <Alert
                  type="error"
                  title={alertTitle}
                  text={alertText}
                  confirmButtonText="Ok"
                  onConfirmButtonClick={handleConfirmAlertButtonClick}
                />
            }
            <ReviewSessionTournamentBracket
              roundCount={tournament?.round_count}
              currentRound={currentRound}
              gameType={tournamentRoundGames[currentRound - 1]?.gameType}
              rounds={tournamentsRounds}
              matchups={tournamentsMatchups}
              teams={tournamentsTeams}
              teamScores={tournamentsTeamScores}
              isScoresUpdated={isScoresUpdated}
              isReviewButtonLoading={isReviewButtonLoading}
              onReviewButtonClick={handleReviewButtonClick}
              isReviewButtonDisabled={isReviewButtonDisabled}
            />
            {celebration?.type === 'leader' && (
              <CelebrationModal
                variant="variant-leader"
                onClose={() => setCelebration(null)}
                content={
                  <LeaderCelebration
                    leadingTeams={celebration.leadingTeams}
                    roundName={celebration.roundName}
                  />
                }
              />
            )}
            {celebration?.type === 'winnersLosers' && (
              <CelebrationModal
                variant="variant-winners-losers"
                onClose={() => setCelebration(null)}
                content={
                  <WinnersLosers
                    winners={celebration.winnerTeams}
                    losers={celebration.loserTeams}
                    roundName={celebration.roundName}
                  />
                }
              />
            )}
            {celebration?.type === 'championshipPair' && (
              <CelebrationModal
                variant={['variant-champions', 'variant-second-place', 'variant-top-performer']}
                onClose={() => setCelebration(null)}
                content={[
                  <Champions
                    key="champions"
                    team={celebration.winnerTeam}
                  />,
                  <SecondPlace
                    key="second-place"
                    team={celebration.loserTeam}
                  />,
                  <TopPerformer
                    key="top-performer"
                    userName={celebration.topPerformerName}
                    totalPoints={celebration.topPerformerPoints}
                  />
                ]}
              />
            )}
          </>
      }
    </Layout>
  );
}
